/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import SearchUtils from '../../utils/SearchUtils';
import { SearchResults } from './SearchResults';
import { ResultSource } from './SearchResultSource';
import { SearchError } from './SearchError';

export class SearchManager {
	searchSources: Array<Searchable>;
	userAccountType: IMSAccountType;

	constructor(sources: Array<Searchable>, userAccountType: IMSAccountType) {
		this.searchSources = sources;
		this.userAccountType = userAccountType;
	}

	async search(
		query: string,
		filter: SelectedUiFilters,
		sort: Sort,
		startIndex: number,
		limit: number,
		flags?: Array<string>,
	): Promise<SearchResults> {
		return new Promise(async (resolve) => {
			const startTime = Date.now();
			// make a Query object
			const queryObj: SearchablesQuery = {
				query,
				filter,
				sort,
			};
			const promises: Promise<SearchResult>[] = [];
			// perform a search query on all the searchables
			this.searchSources.forEach((searchSource) => {
				// add the promise to the promises array
				promises.push(searchSource.search(queryObj, startIndex, limit, flags));
			});
			// wait for all the results
			const results = await Promise.all(promises);
			const duration = Date.now() - startTime;
			const resultSources: ResultSource[] = [];
			let i = 0;
			// create a ResultSource for each result
			// and add them to an array
			this.searchSources.forEach((searchSource) => {
				resultSources.push(
					new ResultSource(
						searchSource,
						queryObj,
						startIndex,
						limit,
						results[i++],
						this.userAccountType,
						flags,
					),
				);
			});
			// Save the timing data to session storage
			SearchUtils.saveSearchTimingInSessionStorage('search-assets', 'server', duration);
			// finally construct a SearchResults object from the Result sources
			resolve(new SearchResults(resultSources, sort, duration));
		});
	}
}

export { ResultSource, SearchResults, SearchError };
